import { Component, computed, inject, input, Signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SortDirection } from '@app/shared/types/enums/sort-direction.enum';
import { TaskSortField } from '@app/shared/types/enums/task-sort-field';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { ProffeoUiSelectOption } from '@app/shared/ui/types/select.types';
import { TranslatePipe } from '@ngx-translate/core';
import { TaskFiltersService } from '../task-filters.service';

/**
 * @summary Compact sort field + direction controls for the task list.
 * @selector proffeo-task-sort-controls
 */
@Component({
  selector: 'proffeo-task-sort-controls',
  imports: [MatIconModule, MatTooltipModule, UiButtonComponent, UiSelectComponent, TranslatePipe],
  templateUrl: './task-sort-controls.component.html',
  styleUrl: './task-sort-controls.component.scss',
  host: {
    '[class.contents]': 'embedded()',
    '[class.task-sort-controls--chip]': 'chip()'
  }
})
export class TaskSortControlsComponent {
  private readonly taskFiltersService = inject(TaskFiltersService);

  protected readonly SortDirection = SortDirection;
  protected readonly orderBy: Signal<TaskSortField> = this.taskFiltersService.orderBy;
  protected readonly order: Signal<SortDirection> = this.taskFiltersService.order;
  protected readonly sortByOptions: Signal<ProffeoUiSelectOption<TaskSortField>[]> =
    this.taskFiltersService.availableSortByOptions;
  protected readonly currentSortLabel = computed(
    () => this.sortByOptions().find(option => option.value === this.orderBy())?.label ?? ''
  );
  protected readonly currentSortChipLabel = computed(() =>
    this.orderBy() === TaskSortField.LastActivityAt ? 'task-list-sort-updated' : this.currentSortLabel()
  );
  protected readonly sortDirectionLabel = computed(() => (this.order() === SortDirection.ASC ? 'asc' : 'desc'));

  /** When true (narrow split-view column), sort controls use full list width. */
  public readonly compact = input(false);
  /** When true, children join the parent grid so the sort button lines up with other icon actions. */
  public readonly embedded = input(false);
  /** Compact mobile chip: field dropdown plus a separate direction toggle inside one frame. */
  public readonly chip = input(false);

  protected toggleSortDir(): void {
    this.taskFiltersService.setOrder(this.order() === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC);
  }

  protected setOrderBy(value: TaskSortField | null): void {
    this.taskFiltersService.setOrderBy(value);
  }
}
